import styled from 'styled-components6';
import { MouseEvent, useMemo } from 'react';
import { MediaType } from 'rwl-shared/dist/utils/media';
import { OutlineButtonNeutral } from '../../../theme';
import { When } from 'react-if';
import { faRectangleHistory } from '@fortawesome/pro-regular-svg-icons/faRectangleHistory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PhotoCollageContainer = styled.div`
    padding-left: var(--rwl-spacing-md);
    padding-right: var(--rwl-spacing-md);
    display: grid;
    gap: var(--rwl-spacing-md);
    position: relative;
    width: 100%;
    aspect-ratio: 2/1;

    grid-template-columns: 2fr 1fr;
    grid-template-rows: auto;

    &.count1 {
        grid-template-columns: 1fr;
    }

    &.count3 > :first-child {
        grid-row: 1 / span 2;
    }
`;

const HasMoreButton = styled(OutlineButtonNeutral)`
    position: absolute;
    bottom: 10px;
    right: 30px;
    z-index: 1;
    background-color: var(--rwl-palette-gray-100);
    color: black;
    &:hover {
        background-color: var(--rwl-palette-gray-200);
    }
`;

const ImageContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: var(--rwl-spacing-sm);
    cursor: pointer;
`;

const Image = styled.img`
    object-fit: cover;
    flex: 1;
    width: 100%;
    height: 100%;
`;

const Video = styled.video`
    object-fit: cover;
    flex: 1;
    width: 100%;
    height: 100%;
`;

type PhotoCollageMediaType = {
    id: string;
    type: MediaType;
    sourceUrl: string;
    [key: string]: any;
};

const CollageImage = ({
    media,
    onClick,
}: {
    media: PhotoCollageMediaType;
    onClick: (e: any) => void;
}) => {
    return (
        <ImageContainer onClick={onClick}>
            {media.type === MediaType.Photo ? (
                <Image
                    className="rwlCollagePhoto"
                    src={media.sourceUrl || media.url}
                    loading="lazy"
                />
            ) : (
                <Video
                    className="rwlCollageVideo"
                    onMouseEnter={(e: MouseEvent) => {
                        (e.target as HTMLVideoElement).play();
                    }}
                    onMouseLeave={(e: MouseEvent) => {
                        (e.target as HTMLVideoElement).pause();
                    }}
                    src={media.sourceUrl || media.url}
                    autoPlay={false}
                    muted={true}
                    loop={true}
                />
            )}
        </ImageContainer>
    );
};

interface PhotoCollageProps {
    media: PhotoCollageMediaType[];
    onClickMedia: (media: any) => void;
    style?: React.CSSProperties;
    onSeeAllMedia?: () => void;
}

export const PhotoCollage = ({
    media,
    onClickMedia,
    style,
    onSeeAllMedia,
}: PhotoCollageProps) => {
    const collageMedia = useMemo(() => {
        return media.slice(0, 3);
    }, [media]);

    if (collageMedia.length === 0) {
        return <div />;
    }

    return (
        <PhotoCollageContainer
            id={'photoCollage'}
            data-theme="dark"
            className={`count${collageMedia.length}`}
            style={style}
        >
            {collageMedia.map((media, idx) => {
                return (
                    <CollageImage
                        key={getMediaKey(media, idx)}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClickMedia(media);
                        }}
                        media={media}
                    />
                );
            })}
            <When condition={media.length > 3}>
                <HasMoreButton
                    key={'more'}
                    onClick={() => {
                        onClickMedia(media[0]);
                        onSeeAllMedia?.();
                    }}
                >
                    <FontAwesomeIcon icon={faRectangleHistory} />
                    More photos
                </HasMoreButton>
            </When>
        </PhotoCollageContainer>
    );
};

/**
 * The same media ID might be in list multiple times (thanks a lot cover photos)
 * so we need to append the index to th key.  Otherwise we get weird errors where
 * not all the DOM nodes get removed because there are key collisions.
 *
 * @param media
 * @param idx
 * @returns
 */
const getMediaKey = (media: PhotoCollageMediaType, idx: number) =>
    `${media.id}-${idx}`;
